import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useDashboardTheme } from "../../../../context/DashboardThemeContext";
import { useAlert } from "../../../../context/AlertContext";
import Calculator from "./Calculator";

function getFactor(iva) {
  const ivaNumber = Number(iva || 0);
  if (ivaNumber === 999) return 1;
  return 1 + ivaNumber / 100;
}

function formatMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function ProductForm({ form, setForm, nextCode, onSubmit }) {
  const { dashboardTheme } = useDashboardTheme();
  const { showError } = useAlert();

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const token = localStorage.getItem("accessToken");

  const ivaValue = useMemo(() => {
    const pvp = Number(form.pvp1 || 0);

    if (Number(form.iva_porcentaje) === 999) {
      return 0;
    }

    return pvp * (Number(form.iva_porcentaje || 0) / 100);
  }, [form.pvp1, form.iva_porcentaje]);

  const finalPrice = useMemo(() => {
    const pvp = Number(form.pvp1 || 0);
    const factor = getFactor(form.iva_porcentaje);
    return pvp * factor;
  }, [form.pvp1, form.iva_porcentaje]);

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const loadOptions = async () => {
    try {
      const [categoriesResponse, brandsResponse] = await Promise.all([
        fetch("/api/products/categories/?estado=activo", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch("/api/products/brands/?estado=activo", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      const categoriesData = await categoriesResponse.json();
      const brandsData = await brandsResponse.json();

      if (categoriesResponse.ok) {
        setCategories(categoriesData.results || []);
      }

      if (brandsResponse.ok) {
        setBrands(brandsData.results || []);
      }
    } catch {
      showError("No se pudieron cargar categorías y marcas");
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  const selectMenuProps = {
    PaperProps: {
      elevation: 0,
      sx: {
        mt: 0.5,
        bgcolor: dashboardTheme.card_background,
        color: dashboardTheme.text_color,
        border: `1px solid ${dashboardTheme.border_color}`,
        boxShadow: "none",
        "& .MuiMenu-list": {
          bgcolor: dashboardTheme.card_background,
          color: dashboardTheme.text_color,
          p: 0.5,
        },
        "& .MuiMenuItem-root": {
          color: dashboardTheme.text_color,
          bgcolor: dashboardTheme.card_background,
          borderRadius: "8px",
          fontSize: 14,
          "&:hover": {
            bgcolor: dashboardTheme.dashboard_background,
          },
          "&.Mui-selected": {
            bgcolor: dashboardTheme.dashboard_background,
            color: dashboardTheme.text_color,
          },
          "&.Mui-selected:hover": {
            bgcolor: dashboardTheme.dashboard_background,
          },
          "&.Mui-focusVisible": {
            bgcolor: dashboardTheme.dashboard_background,
          },
        },
      },
    },
  };

  const selectSx = {
    ...dashboardTheme.inputStyles,
    "& .MuiSelect-icon": {
      color: dashboardTheme.text_color,
    },
  };

  const sectionSx = {
    borderRadius: "14px",
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
    p: { xs: 1.5, md: 2 },
  };

  const gridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
    gap: 2,
  };

  return (
    <>
      <Paper elevation={0} sx={sectionSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 19, md: 21 },
              fontWeight: 600,
              color: dashboardTheme.text_color,
            }}
          >
            Datos del producto
          </Typography>

          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            onClick={onSubmit}
            sx={dashboardTheme.buttonStyles.contained}
          >
            Guardar producto
          </Button>
        </Box>

        <Stack spacing={2.3}>
          <Box sx={gridSx}>
            <TextField
              fullWidth
              size="small"
              label="Código"
              value={nextCode || "Se asignará automáticamente"}
              disabled
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="Nombre"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              sx={dashboardTheme.inputStyles}
            />
          </Box>

          <Box sx={gridSx}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={form.estado}
                onChange={(e) => updateField("estado", e.target.value)}
                MenuProps={selectMenuProps}
              >
                <MenuItem value="activo">Activo</MenuItem>
                <MenuItem value="inactivo">Inactivo</MenuItem>
              </Select>
            </FormControl>

            <Box
              sx={{
                borderRadius: "12px",
                border: `1px solid ${dashboardTheme.border_color}`,
                px: 1.5,
                minHeight: 40,
                display: "flex",
                alignItems: "center",
                bgcolor: dashboardTheme.input_background,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.ecommerce}
                    onChange={(e) => updateField("ecommerce", e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: dashboardTheme.primary_color,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: dashboardTheme.primary_color,
                      },
                    }}
                  />
                }
                label="Ecommerce"
                sx={{
                  color: dashboardTheme.text_color,
                  m: 0,
                }}
              />
            </Box>
          </Box>

          <Box sx={gridSx}>
            <Autocomplete
              options={categories}
              value={form.category}
              onChange={(_event, value) => updateField("category", value)}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              noOptionsText="Sin categorías"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Categoría"
                  sx={dashboardTheme.inputStyles}
                />
              )}
              PaperComponent={(props) => (
                <Paper
                  {...props}
                  elevation={0}
                  sx={{
                    bgcolor: dashboardTheme.card_background,
                    color: dashboardTheme.text_color,
                    border: `1px solid ${dashboardTheme.border_color}`,
                    boxShadow: "none",
                    "& .MuiAutocomplete-option": {
                      color: dashboardTheme.text_color,
                      bgcolor: dashboardTheme.card_background,
                      "&:hover": {
                        bgcolor: dashboardTheme.dashboard_background,
                      },
                      "&[aria-selected='true']": {
                        bgcolor: dashboardTheme.dashboard_background,
                      },
                    },
                  }}
                />
              )}
            />

            <Autocomplete
              options={brands}
              value={form.brand}
              onChange={(_event, value) => updateField("brand", value)}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              noOptionsText="Sin marcas"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Marca"
                  sx={dashboardTheme.inputStyles}
                />
              )}
              PaperComponent={(props) => (
                <Paper
                  {...props}
                  elevation={0}
                  sx={{
                    bgcolor: dashboardTheme.card_background,
                    color: dashboardTheme.text_color,
                    border: `1px solid ${dashboardTheme.border_color}`,
                    boxShadow: "none",
                    "& .MuiAutocomplete-option": {
                      color: dashboardTheme.text_color,
                      bgcolor: dashboardTheme.card_background,
                      "&:hover": {
                        bgcolor: dashboardTheme.dashboard_background,
                      },
                      "&[aria-selected='true']": {
                        bgcolor: dashboardTheme.dashboard_background,
                      },
                    },
                  }}
                />
              )}
            />
          </Box>

          <Box sx={gridSx}>
            <TextField
              fullWidth
              size="small"
              label="Código de barras"
              value={form.barcode}
              onChange={(e) => updateField("barcode", e.target.value)}
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="Código auxiliar"
              value={form.codigo_auxiliar}
              onChange={(e) => updateField("codigo_auxiliar", e.target.value)}
              sx={dashboardTheme.inputStyles}
            />
          </Box>

          <Box sx={gridSx}>
            <TextField
              fullWidth
              size="small"
              label="PVP"
              type="number"
              value={form.pvp1}
              onChange={(e) => updateField("pvp1", e.target.value)}
              sx={dashboardTheme.inputStyles}
              inputProps={{
                step: "0.000001",
                min: "0",
              }}
            />

            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>IVA</InputLabel>
              <Select
                label="IVA"
                value={form.iva_porcentaje}
                onChange={(e) => updateField("iva_porcentaje", e.target.value)}
                MenuProps={selectMenuProps}
              >
                <MenuItem value={0}>0%</MenuItem>
                <MenuItem value={5}>5%</MenuItem>
                <MenuItem value={15}>15%</MenuItem>
                <MenuItem value={999}>No objeto de IVA</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={gridSx}>
            <TextField
              fullWidth
              size="small"
              label="Valor IVA"
              value={`$${formatMoney(ivaValue)}`}
              disabled
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="Precio final"
              value={`$${formatMoney(finalPrice)}`}
              disabled
              sx={dashboardTheme.inputStyles}
            />
          </Box>

          <Box sx={gridSx}>
            <TextField
              fullWidth
              size="small"
              label="Stock mínimo"
              type="number"
              value={form.stock_minimo}
              onChange={(e) => updateField("stock_minimo", e.target.value)}
              sx={dashboardTheme.inputStyles}
              inputProps={{
                step: "0.01",
                min: "0",
              }}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Descripción"
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            sx={dashboardTheme.inputStyles}
          />
        </Stack>
      </Paper>

      <Calculator />
    </>
  );
}